import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { convertTimeValueToTimeString } from "@/modules/duration/timeConverter"
import { cn } from "@/modules/lib/utils"
import { ChronoRecapBloc } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/chronoRecapBloc"
import {
  convertChronoFormikToChronoDraft,
  type ExerciseFormikType,
  type ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import type { SimpleFormik } from "@/typings/formik"
import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { ForTimeIcon } from "@images/svgIcons/forTimeIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { displayTotalTime } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { useMemo } from "react"

export function ChronoFortimeRecap({
  formik,
  className,
  ...props
}: { formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey> } & React.ComponentProps<"div">) {
  const displayChronoTime = useMemo(() => {
    const chronoDraft = convertChronoFormikToChronoDraft(formik.values)

    if (chronoDraft === undefined) return word("chrono.title.no_chrono")

    return displayTotalTime(chronoDraft)
  }, [formik.values])

  const onDeleteChronoClick = () => {
    formik.setFieldValue("chronoForTime", undefined)
    formik.setFieldValue("chrono", undefined)
    formik.setFieldValue("chronoType", undefined)
  }

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      <div className="mb-2 flex flex-row items-start justify-between">
        <ForTimeIcon color="currentColor" className="text-textOnColor-body" />

        <div className="flex flex-col items-center">
          <Text fontStyle="LS">{word("chrono.title.for_time")}</Text>

          <div className="flex items-center">
            <ChronoIcon color="currentColor" className="text-textDefault-body" />
            <Text className="ml-2" fontStyle="TL">
              {displayChronoTime}
            </Text>
          </div>
        </div>

        <Button variant="ghost" size="iconMd" onClick={onDeleteChronoClick}>
          <TrashIcon color="currentColor" className="text-textDefault-body" width={22} height={22} />
        </Button>
      </div>

      {formik.values.chronoForTime?.trainingTime && (
        <>
          <div className="mb-2 flex items-center justify-between gap-2">
            {formik.values.chronoForTime.numberOfSets > 1 && (
              <ChronoRecapBloc
                setsIcon
                value={`${formik.values.chronoForTime.numberOfSets} ${word("global.score.set.unit", { count: formik.values.chronoForTime.numberOfSets })}`}
              />
            )}
            <ChronoRecapBloc
              playIcon
              infinityIcon={formik.values.chronoForTime.noLimit}
              value={
                formik.values.chronoForTime.noLimit
                  ? ""
                  : convertTimeValueToTimeString(formik.values.chronoForTime.trainingTime, "00m 00s")
              }
            />
          </div>
          {formik.values.chronoForTime.numberOfSets > 1 && (
            <div className="mb-2 flex items-center justify-between gap-2">
              <ChronoRecapBloc
                circleBreakIcon
                value={`${convertTimeValueToTimeString(formik.values.chronoForTime.restTime, "00m 00s")} ${word("chrono.settings.label.rest_between_sets_short")}`}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
